<template>
  <v-tooltip v-if="showTooltip" bottom>
    <template v-slot:activator="{ on, attrs }">
      <v-avatar
        :size="size"
        :color="hasPhoto ? undefined : 'primary'"
        class="user-avatar-clickable"
        v-bind="attrs"
        v-on="on"
        @click="navigateToProfile"
      >
        <v-img
          v-if="hasPhoto"
          :src="avatarImageUrl"
          :alt="user.displayName || user.email" 
          @error="onImageError"
        />
        <span
          v-else-if="avatarText"
          class="white--text text-h6"
        >
          {{ avatarText }}
        </span>
        <v-icon v-else color="white">
          mdi-account
        </v-icon>
      </v-avatar>
    </template>
    <span>{{ tooltipText }}</span>
  </v-tooltip>

  <v-avatar
    v-else
    :size="size"
    :color="hasPhoto ? undefined : 'primary'"
    class="user-avatar-clickable"
    @click="navigateToProfile"
  >
    <v-img
      v-if="hasPhoto"
      :src="avatarImageUrl"
      :alt="user.displayName || user.email"
      @error="onImageError"
    />
    <span
      v-else-if="avatarText"
      class="white--text text-h6"
    >
      {{ avatarText }}
    </span>
    <v-icon v-else color="white">
      mdi-account
    </v-icon>
  </v-avatar>
</template>

<script>
import firebase from 'firebase/compat/app'
import 'firebase/compat/storage'

export default {
  name: 'UserAvatar',

  props: {
    user: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    size: {
      type: Number,
      default: 40,
    },
    showTooltip: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      imageLoadError: false,
      avatarImageUrl: null,
    }
  },

  computed: {
    hasPhoto() {
      return !!(this.avatarImageUrl && !this.imageLoadError)
    },

    avatarText() {
      if (!this.user) return ''
      
      const displayName = this.user.displayName
      if (!displayName) return ''

      return this.getInitials(displayName)
    },

    tooltipText() {
      if (!this.user) return ''

      const name = this.user.displayName || this.user.email || 'User'
      const email = this.user.email || ''

      if (this.user.displayName && this.user.email) {
        return `${name} - (${email})`
      }

      return name
    },
  },

  watch: {
    user: {
      immediate: true,
      deep: true,
      handler(newUser) {
        if (newUser) {
          this.loadAvatarUrl()
        }
      }
    }
  },

  mounted() {
    this.loadAvatarUrl()
  },

  methods: {
    async loadAvatarUrl() {
      // Prevent multiple simultaneous loads
      if (this._loading) return
      this._loading = true

      try {
        // Reset error state
        this.imageLoadError = false

        // Don't reset avatarImageUrl to null to avoid flicker
        let newUrl = null

        // Check for avatarUrl from Firestore (storage path)
        if (this.user?.avatarUrl) {
          // avatarUrl is a storage path, fetch the download URL
          // Note: Automatically uses emulator when VUE_APP_USE_LOCAL_EMULATORS=true
          // (configured in main.js)
          const storageRef = firebase.storage().ref()
          const logoRef = storageRef.child(this.user.avatarUrl)
          newUrl = await logoRef.getDownloadURL()
        } else if (this.user?.photoURL) {
          // photoURL is already a full URL from Firebase Auth
          newUrl = this.user.photoURL
        }

        this.avatarImageUrl = newUrl
      } catch (error) {
        // console.error('Error loading avatar URL:', error)
        this.imageLoadError = true
        
        // Fallback to photoURL if avatarUrl fails
        if (this.user?.photoURL) {
          this.avatarImageUrl = this.user.photoURL
        } else {
          this.avatarImageUrl = null
        }
      } finally {
        this._loading = false
      }
    },

    getInitials(name) {
      if (!name) return ''

      const words = name.trim().split(/\s+/)
      
      if (words.length === 1) {
        // Single word: take first two characters
        return words[0].substring(0, 2).toUpperCase()
      }

      // Multiple words: take first character of first two words
      return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase()
    },

    onImageError() {
      this.imageLoadError = true
    },

    navigateToProfile() {
      // Navigate to user profile page
      if (this.$route.path !== '/pages/user') {
        this.$router.push('/pages/user')
      }
    },
  },
}
</script>

<style scoped>
.user-avatar-clickable {
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.user-avatar-clickable:hover {
  opacity: 0.8;
}
</style>
